// Compact 8x8 tile heatmap summarising every child occurrence of a
// recurring meeting, rendered on the parent row. Mirrors the deliverable
// OccurrenceStrip layout but uses a meeting-native palette and tooltip
// vocabulary: held → emerald (matches the Held status pill), overdue →
// red, due-soon → brand violet, scheduled → muted, cancelled → muted.
// Caps at 16 tiles with an "+N more" affordance for older occurrences.

import type { MeetingDerivedStatus } from "../../../../types";

export interface MeetingOccurrenceTile {
  id: string;
  status: MeetingDerivedStatus;
  period: string;
}

export interface MeetingOccurrenceStripProps {
  occurrences: MeetingOccurrenceTile[];
}

const MAX_VISIBLE = 16;

const TILE_CLASS: Record<MeetingDerivedStatus, string> = {
  held: "bg-emerald-600 dark:bg-emerald-400",
  overdue: "bg-red-600 dark:bg-red-400",
  "due-soon": "bg-brand-600 dark:bg-brand-400",
  scheduled: "bg-slate-400/40 dark:bg-slate-500/40",
  cancelled: "bg-slate-400/30 dark:bg-slate-500/30",
};

const STATUS_LABEL: Record<MeetingDerivedStatus, string> = {
  held: "held",
  overdue: "overdue",
  "due-soon": "due soon",
  scheduled: "scheduled",
  cancelled: "cancelled",
};

function composeAriaLabel(occurrences: MeetingOccurrenceTile[]): string {
  const counts: Partial<Record<MeetingDerivedStatus, number>> = {};
  for (const o of occurrences) {
    counts[o.status] = (counts[o.status] ?? 0) + 1;
  }
  const parts: string[] = [`${occurrences.length} occurrences`];
  if (counts.held) parts.push(`${counts.held} held`);
  if (counts.overdue) parts.push(`${counts.overdue} overdue`);
  if (counts["due-soon"]) parts.push(`${counts["due-soon"]} due soon`);
  if (counts.scheduled) parts.push(`${counts.scheduled} scheduled`);
  if (counts.cancelled) parts.push(`${counts.cancelled} cancelled`);
  return parts.join(", ");
}

export default function MeetingOccurrenceStrip({
  occurrences,
}: MeetingOccurrenceStripProps) {
  if (occurrences.length === 0) return null;

  const visible =
    occurrences.length > MAX_VISIBLE
      ? occurrences.slice(-MAX_VISIBLE)
      : occurrences;
  const overflow = occurrences.length - visible.length;

  return (
    <div
      data-testid="occurrence-strip"
      role="img"
      aria-label={composeAriaLabel(occurrences)}
      className="inline-flex items-center gap-0.5"
    >
      {visible.map((t) => (
        <span
          key={t.id}
          data-testid={`occurrence-tile-${t.id}`}
          title={`${t.period} · ${STATUS_LABEL[t.status]}`}
          className={`inline-block w-2 h-2 rounded-sm ${TILE_CLASS[t.status]}`}
        />
      ))}
      {overflow > 0 && (
        <span
          data-testid="occurrence-strip-more"
          className="ml-1 text-caption text-slate-500 dark:text-slate-400"
        >
          +{overflow} more
        </span>
      )}
    </div>
  );
}
